/*
When a user navigates to the Partners page
Then they will be presented with a list of partners of Love Big
When a user views a card
Then they will see basic info on a specific partner  (Name, location, description, partner profile pic)
When the user clicks "View Partner"
Then they will be directed to the detailed page for that specific partner
When the user clicks "See More" in the list of partners
Then four more partners will be displayed, if they exist
*/

//Packages
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
//API Route
import { Route } from "../../utils/config";
//Components
import { Link } from "react-router-dom";
import PartnerCard from "./PartnerCard";
//Material UI Components
import {
  Grid,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Select,
  MenuItem,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import "./styles.css";
//Animation
import { motion } from "framer-motion";
//Assets

//Material UI Styles
const useStyles = makeStyles(() => ({
  takeActionDiv: {
    color: "black",
    position: "relative",
    width: "109%",
  },
  takeActionText: {
    textAlign: "center",
  },
  partnerButton: {
    backgroundColor: "#104d82",
    width: "fit-content",
    fontSize: "16px",
    padding: "10px 15px 10px 15px",
    borderRadius: "50px",
  },
}));

export default function Partners() {
  const styles = useStyles();

  //Partner Data stored in state
  const [partners, setPartners] = useState([]);

  //Data for dropdown menus
  const [types, setTypes] = useState([]);
  const [locations, setLocations] = useState([]);

  //Selected search parameters that will be used to query projects
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedContinent, setSelectedContinent] = useState("");
  //Boolean to check if list is finished. If listFinished then the user cannot load
  //more data and the page will reflect that under the list of partners.
  const [listFinished, setListFinished] = useState(false);
  const [isLoading, setIsloading] = useState(true);

  //Ref to determine which page of the partner data the user is currently loading
  const pageRef = useRef(1);

  function getData() {
    pageRef.current++;
    axios
      .post(`${Route}/Partners/GetPartnersToExplore`, {
        pageNumber: 1,
        pageSize: 4,
        projectLocation: "",
        projectType: "",
      })
      .then((res) => {
        setPartners(res.data.partners);
        setLocations(res.data.locations);
        setIsloading(false);
      })
      .catch((err) => {
        throw err;
      });
  }

  const clearFilter = () => {
    setSelectedProject("");
    setSelectedLocation("");
    setSelectedContinent("");
    searchPartners(true);
  };

  const searchPartners = (isClear = false) => {
    setIsloading(true);
    axios
      .post(`${Route}/Partners/GetPartnersToExplore`, {
        pageNumber: 1,
        pageSize: 4,
        projectLocation: isClear ? "" : selectedLocation,
        projectType: isClear ? "" : selectedProject,
      })
      .then((res) => {
        pageRef.current++;
        if (res.data.partners.length > 0) {
          setPartners(res.data.partners);
          setListFinished(true);
        } else {
          setPartners([]);
          setListFinished(true);
        }
        setIsloading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsloading(false);
        throw err;
      });
  };

  const seeMore = () => {
    axios
      .post(`${Route}/Partners/GetPartnersToExplore`, {
        pageNumber: pageRef.current,
        pageSize: 4,
        projectLocation: "",
        projectType: "",
      })
      .then((res) => {
        pageRef.current++;
        if (res.data.partners.length > 0) {
          setPartners(partners.concat(res.data.partners));
        } else {
          pageRef.current = 0;
          setListFinished(true);
        }
      })
      .catch((err) => {
        throw err;
      });
  };

  const getDropdownValues = () => {
    axios
      .get(`${Route}/Application/GetSystemData`)
      .then((res) => {
        setTypes(res.data.specialties);
      })
      .catch((err) => {
        throw err;
      });
  };

  useEffect(() => {
    getData();
    getDropdownValues();
    window.scrollTo(0, 0);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="partner-header">
        <Typography variant="h3" color="white" style={{ marginBottom: "30px" }}>
          Our Partners
        </Typography>
        <Typography
          variant="h5"
          color="white"
          style={{ textAlign: "center" }}
          width="70%"
        >
          Love Big connects and coalesces great hearts and minds to create
          synergy around resolving the greatest needs.
        </Typography>
      </div>
      <Grid container justifyContent="center" mt={2} className="partners-page">
        <Grid item xs={10} mb={2}>
          <div className="filter-panel">
            <Select
              value={selectedContinent}
              sx={{ borderRadius: 100 }}
              displayEmpty
              onChange={(e) => setSelectedContinent(e.target.value)}
              inputProps={{ "aria-label": "Without label" }}
            >
              <MenuItem value="">Continent</MenuItem>
              <MenuItem value="africa">Africa</MenuItem>
              <MenuItem value="asia">Asia</MenuItem>
              <MenuItem value="oceana">Oceana</MenuItem>
              <MenuItem value="south-america">South America</MenuItem>
              <MenuItem value="north-america">North America</MenuItem>
            </Select>
            <Select
              value={selectedLocation}
              sx={{ borderRadius: 100 }}
              onChange={(e) => setSelectedLocation(e.target.value)}
              displayEmpty
              inputProps={{ "aria-label": "Without label" }}
            >
              <MenuItem value="">
                <i className="fas fa-location-arrow"></i> Location
              </MenuItem>

              {locations.map((location) => {
                return (
                  <MenuItem value={location} key={location}>
                    {location}
                  </MenuItem>
                );
              })}
            </Select>

            <Select
              value={selectedProject}
              sx={{ borderRadius: 100 }}
              onChange={(e) => setSelectedProject(e.target.value)}
              displayEmpty
              inputProps={{ "aria-label": "Without label" }}
            >
              <MenuItem value="" disabled>
                Project Type
              </MenuItem>
              <MenuItem value="" key="all">
                All
              </MenuItem>
              {types.map((type) => {
                return (
                  <MenuItem value={type.name} key={type.id}>
                    {type.name}
                  </MenuItem>
                );
              })}
            </Select>
            <Button
              color="primary"
              variant="contained"
              size="medium"
              onClick={() => {
                searchPartners();
              }}
            >
              Search Partners
            </Button>
            <Button
              color="secondary"
              size="small"
              onClick={() => {
                clearFilter();
              }}
            >
              All
            </Button>
          </div>
        </Grid>
        <Grid item xs={10}>
          <Grid container item justifyContent="center">
            {!isLoading ? (
              <>
                <Grid container spacing={3} mb={4}>
                  {partners.map((partner) => {
                    return (
                      <PartnerCard
                        key={partner.id}
                        id={partner.id}
                        logoUrl={partner.logoUrl}
                        companyName={partner.companyName}
                        region={partner.region}
                        description={partner.description}
                      ></PartnerCard>
                    );
                  })}
                </Grid>
                {listFinished ? (
                  <div className="no-projects">
                    No more partners to display.
                  </div>
                ) : (
                  <div onClick={() => seeMore()} className="more-projects-btn">
                    See more
                  </div>
                )}
              </>
            ) : (
              <Grid container justifyContent="center" mt={2} mb={2}>
                <Grid item>
                  <CircularProgress></CircularProgress>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid
          container
          item
          xs={10}
          className={styles.takeActionDiv}
          mb={4}
          pt={4}
          pb={4}
        >
          <Grid container item className={styles.takeActionText}>
            <Stack spacing={2} width="100%">
              <Typography
                variant="h6"
                fontSize={{ sm: "34px" }}
                fontWeight="400"
                lineHeight={{ xs: "22px", sm: "37px" }}
              >
                Join Our Collective
              </Typography>

              <Link to="/join" style={{ textDecoration: "none" }}>
                <Button variant="contained" color="light">
                  Become a Partner
                </Button>
              </Link>
            </Stack>
          </Grid>
        </Grid>
      </Grid>
    </motion.div>
  );
}
